import {Capacitor, registerPlugin} from '@capacitor/core';
import {ref} from 'vue';
import type {ICallerIdDirectory} from '@torim-app/types';

import {CustomersApi} from '@app/api/admin/customers-api';
import {useStorage} from '@app/composables/use-storage';

/**
 * Caller ID — recognise incoming calls from customers.
 *
 * The phone keeps a local copy of the business's customer directory (name, next
 * appointment, debt…) because the OS gives us only seconds while it rings, offline:
 *  - Android: CallScreeningService → floating card over the call screen + notification
 *    that opens the customer card (android/app/src/main/java/com/timeit/app/callerid).
 *  - iOS: Call Directory extension → label on the incoming-call screen
 *    (ios/App/App/CallerId + ios/App/CallDirectoryExtension).
 *
 * The directory is refreshed on app launch and whenever the app returns to the
 * foreground (throttled), and wiped on logout or when the owner turns it off.
 */

export interface ICallerIdStatus {
  platform: 'android' | 'ios';
  /** The device/build can do caller ID at all. */
  supported: boolean;
  /** Incoming calls will be identified (Android: role granted, iOS: extension switched on). */
  ready: boolean;
  cachedEntries: number;
  syncedAt?: number;
  // Android
  screeningRole?: boolean;
  contacts?: boolean;
  notifications?: boolean;
  overlay?: boolean;
  // iOS
  extensionEnabled?: boolean;
  reloadError?: string;
}

interface CallerIdPlugin {
  getStatus(): Promise<ICallerIdStatus>;
  sync(options: {
    directory: ICallerIdDirectory;
    showOverlay?: boolean;
    showNotification?: boolean;
    notifyDesktop?: boolean;
    apiUrl?: string;
  }): Promise<ICallerIdStatus>;
  clear(): Promise<ICallerIdStatus>;
  // Android
  requestScreeningRole(): Promise<ICallerIdStatus>;
  requestContactsPermission(): Promise<ICallerIdStatus>;
  requestNotificationPermission(): Promise<ICallerIdStatus>;
  openOverlaySettings(): Promise<ICallerIdStatus>;
  preview(options?: { phone?: string }): Promise<ICallerIdStatus>;
  // iOS
  openSettings(): Promise<ICallerIdStatus>;
}

export const CallerId = registerPlugin<CallerIdPlugin>('CallerId');

// ─── "Appointments changed" → refresh the on-device list ─────────────────────
// The phone answers calls from its cached list (iOS can't ask us at call time), so after
// the owner books/edits/cancels in the app we re-sync right away. Admin.vue registers the
// listener (it knows whether syncing is allowed: not while impersonating, contact access).
let appointmentsChangedListener: (() => void) | undefined;
let appointmentsChangedTimer: ReturnType<typeof setTimeout> | undefined;

export const onAppointmentsChanged = (listener: (() => void) | undefined) => {
  appointmentsChangedListener = listener;
};

/** Called by the appointments API after a successful change; debounced so bulk edits sync once. */
export const notifyAppointmentsChanged = () => {
  if (!Capacitor.isNativePlatform()) return;
  clearTimeout(appointmentsChangedTimer);
  appointmentsChangedTimer = setTimeout(() => appointmentsChangedListener?.(), 2_000);
};

const STORAGE_KEY = 'caller-id-settings';
// Foreground syncs are cheap (one request) and the iOS label is only as fresh as the last
// sync, so keep this short — it only de-dupes bursts of resume events.
const MIN_SYNC_INTERVAL_MS = 60 * 1000;

export interface ICallerIdSettings {
  enabled: boolean;
  /** Android only — floating card over the call screen. */
  showOverlay: boolean;
  /** Android only — tappable notification. */
  showNotification: boolean;
  /** Android only — also show the card on the business's open desktop dashboards. */
  notifyDesktop: boolean;
}

const DEFAULT_SETTINGS: ICallerIdSettings = {enabled: false, showOverlay: true, showNotification: true, notifyDesktop: true};

// Module-level so the settings screen and Admin.vue share one state.
const status = ref<ICallerIdStatus | null>(null);
const settings = ref<ICallerIdSettings>({...DEFAULT_SETTINGS});
const syncing = ref(false);
let settingsLoaded = false;
let lastSyncAt = 0;
let inFlight: Promise<ICallerIdStatus | null> | null = null;

const isAvailable = (): boolean =>
  Capacitor.isNativePlatform() && Capacitor.isPluginAvailable('CallerId');

export const useCallerId = () => {
  const {getValue, setValue} = useStorage();

  const loadSettings = async (): Promise<ICallerIdSettings> => {
    if (settingsLoaded) return settings.value;
    try {
      const raw = await getValue(STORAGE_KEY);
      settings.value = raw ? {...DEFAULT_SETTINGS, ...JSON.parse(raw)} : {...DEFAULT_SETTINGS};
    } catch {
      settings.value = {...DEFAULT_SETTINGS};
    }
    settingsLoaded = true;
    return settings.value;
  };

  const saveSettings = async (patch: Partial<ICallerIdSettings>) => {
    await loadSettings();
    settings.value = {...settings.value, ...patch};
    await setValue(STORAGE_KEY, JSON.stringify(settings.value));
  };

  const refreshStatus = async (): Promise<ICallerIdStatus | null> => {
    if (!isAvailable()) return null;
    try {
      status.value = await CallerId.getStatus();
    } catch (e) {
      console.error('[caller-id] getStatus failed', e);
    }
    return status.value;
  };

  /** Pull the directory from the server and hand it to the native layer. */
  const sync = async ({force = false} = {}): Promise<ICallerIdStatus | null> => {
    if (!isAvailable()) return null;
    await loadSettings();
    if (!settings.value.enabled) return null;
    if (!force && Date.now() - lastSyncAt < MIN_SYNC_INTERVAL_MS) return status.value;
    if (inFlight) return inFlight;

    syncing.value = true;
    inFlight = (async () => {
      try {
        const directory = await CustomersApi.fetchCallerIdDirectory();
        status.value = await CallerId.sync({
          directory,
          showOverlay: settings.value.showOverlay,
          showNotification: settings.value.showNotification,
          notifyDesktop: settings.value.notifyDesktop,
          apiUrl: import.meta.env.VITE_BACKEND_URL,
        });
        lastSyncAt = Date.now();
      } catch (e) {
        console.error('[caller-id] sync failed', e);
        await refreshStatus();
        return null; // callers treat null as failure — don't report stale status as success
      } finally {
        syncing.value = false;
        inFlight = null;
      }
      return status.value;
    })();
    return inFlight;
  };

  const enable = async () => {
    await saveSettings({enabled: true});
    return sync({force: true});
  };

  /** Turn off and wipe the cached customers from the device. */
  const disable = async () => {
    await saveSettings({enabled: false});
    lastSyncAt = 0;
    if (!isAvailable()) return;
    try {
      status.value = await CallerId.clear();
    } catch (e) {
      console.error('[caller-id] clear failed', e);
    }
  };

  /** On logout: the next user of this phone must not inherit this business's customers. */
  const clearOnLogout = async () => {
    if (!isAvailable()) return;
    await disable();
  };

  return {
    isAvailable: isAvailable(),
    platform: Capacitor.getPlatform(),
    status,
    settings,
    syncing,
    loadSettings,
    saveSettings,
    refreshStatus,
    sync,
    enable,
    disable,
    clearOnLogout,
  };
};
